/**
 * Google Analytics 4 con Consent Mode v2.
 * Regole: caricato una sola volta, mai su /admin (né sulle anteprime private delle bozze),
 * analytics_storage negato finché l'utente non accetta, nessun dato personale negli eventi.
 */
const MEASUREMENT_ID = "G-BM6DPLP1J1";
const CONSENT_KEY = "oa-consenso-analytics";

let loaded = false;

export const EXCLUDED_PREFIXES = ["/admin"];

export function isTrackablePath(pathname = window.location.pathname) {
  return !EXCLUDED_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

export function readConsent() {
  try {
    return localStorage.getItem(CONSENT_KEY); // "granted" | "denied" | null
  } catch {
    return null;
  }
}

/**
 * Solo questi parametri di URL possono arrivare a Google: sono i marcatori di campagna.
 * Tutto il resto viene scartato, così nessun dato personale finito per sbaglio nella query
 * può essere inviato.
 */
const CAMPAIGN_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "gclid",
  "gbraid",
  "wbraid",
];

/** Query string ripulita: solo i parametri di campagna, nell'ordine della whitelist. */
export function campaignQuery(search = window.location.search) {
  const src = new URLSearchParams(search);
  const out = new URLSearchParams();
  for (const key of CAMPAIGN_PARAMS) {
    const value = src.get(key);
    if (value) out.set(key, value);
  }
  const qs = out.toString();
  return qs ? `?${qs}` : "";
}

// Origine della sessione: solo in memoria, nessun cookie e nessuno storage,
// così non tocchiamo in alcun modo il Consent Mode.
let sessionOrigin = null;

/** Registra l'origine dalla prima pagina vista (la prima vince: è la sorgente della sessione). */
export function captureOrigin(search = window.location.search) {
  if (sessionOrigin) return sessionOrigin;
  const campaign = new URLSearchParams(search).get("utm_campaign");
  if (campaign === "google_business_profile") sessionOrigin = "google_business_profile";
  return sessionOrigin;
}

/** Origine non personale da allegare agli eventi di prenotazione, se nota. */
export function bookingOriginParams() {
  return sessionOrigin ? { booking_origin: sessionOrigin } : {};
}

function gtag() {
  window.dataLayer = window.dataLayer || [];
  // gtag.js riconosce i comandi solo se nel dataLayer arriva l'oggetto `arguments`:
  // con un array normale (es. push([...])) i comandi vengono ignorati e non parte nessun hit.
  window.dataLayer.push(arguments);
}

/** Carica il tag una sola volta, con consenso negato di default. */
export function initAnalytics() {
  if (loaded || typeof window === "undefined" || !isTrackablePath()) return;
  loaded = true;
  captureOrigin();
  window.gtag = window.gtag || gtag;

  // Consent Mode v2: tutto negato finché l'utente non accetta.
  gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    functionality_storage: "granted",
    security_storage: "granted",
    wait_for_update: 500,
  });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(script);

  gtag("js", new Date());
  // Il page_view lo inviamo noi a ogni cambio di route: così non ce ne sono doppi.
  gtag("config", MEASUREMENT_ID, { send_page_view: false, anonymize_ip: true });

  if (readConsent() === "granted") updateConsent(true);
}

export function updateConsent(granted) {
  gtag("consent", "update", {
    analytics_storage: granted ? "granted" : "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
  try {
    localStorage.setItem(CONSENT_KEY, granted ? "granted" : "denied");
  } catch {
    /* archiviazione non disponibile: il consenso vale per la sessione corrente */
  }
}

export function trackPageView(pathname, title) {
  if (!isTrackablePath(pathname)) return;
  captureOrigin();
  // I parametri di campagna restano in page_location: è così che GA4 attribuisce la visita
  // (source/medium/campaign/content) con il suo normale parsing, senza forzature manuali.
  gtag("event", "page_view", {
    page_path: pathname,
    page_location: `${window.location.origin}${pathname}${campaignQuery()}`,
    page_title: title || document.title,
  });
}

/** Eventi personalizzati: solo dati non personali (mai nome, email, telefono, riferimento). */
export function trackEvent(name, params = {}) {
  if (!isTrackablePath()) return;
  gtag("event", name, params);
}

const onceSent = new Set();

/** Come trackEvent, ma una sola volta per chiave (evita i doppi invii dei doppi render). */
export function trackEventOnce(key, name, params = {}) {
  if (onceSent.has(key)) return;
  onceSent.add(key);
  trackEvent(name, params);
}

export const MEASUREMENT_ID_FOR_TEST = MEASUREMENT_ID;
